import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useShop } from '@/state';
import { track } from '@/lib/analytics';
import {
  formatPrice, formatDateHuman, pluralize, DELIVERY_LABEL, ORDER_STATUS_LABEL,
} from '@alkor/utils';
import { openManagerChat } from '@/lib/telegram';
import { IconMessage } from '@/components/icons';
import { OrderStatusChip, ErrorState, TileSkeleton, ProductImage } from '@/components/ui';
import { isNetworkError, isPaused } from '@/lib/online';

const MANAGER = (import.meta.env.VITE_MANAGER_USERNAME as string | undefined) ?? 'alkor_manager';

function timeOf(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function OrderDetailScreen() {
  const { number = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { showToast } = useShop();

  const query = useQuery({
    queryKey: ['order', number],
    queryFn: () => api.order(number),
    staleTime: 30_000,
  });

  const repeat = async () => {
    try {
      const res = await api.repeatOrder(number);
      await qc.invalidateQueries({ queryKey: ['cart'] });
      // Честный ответ вместо тихого пропуска недоступных позиций
      showToast(
        res.unavailable.length === 0
          ? `Добавлено позиций: ${res.added.length}`
          : `${res.added.length} из ${res.added.length + res.unavailable.length} позиций добавлены. Недоступно: ${res.unavailable.join(', ')}`,
      );
      navigate('/cart');
    } catch {
      showToast('Не удалось повторить заказ');
    }
  };

  if (isPaused(query)) {
    return <ErrorState offline onRetry={() => void query.refetch()} />;
  }
  if (query.isLoading) {
    return <div className="block" style={{ marginTop: 20 }}><TileSkeleton count={3} /></div>;
  }
  if (query.isError || !query.data) {
    return (
      <ErrorState
        message="Заказ не найден"
        offline={isNetworkError(query.error)}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const o = query.data;
  const history = o.history ?? [];
  const deliveryLine = [
    DELIVERY_LABEL[o.delivery],
    o.deliveryCity,
    o.deliveryAddress,
    o.deliveryCompany,
  ].filter(Boolean).join(', ');

  return (
    <>
      <div className="screen-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--f-mono)' }}>{o.orderNumber}</span>
        <OrderStatusChip status={o.status} />
      </div>
      <div className="screen-sub">
        {formatDateHuman(o.createdAt)} · {pluralize(o.items.length, 'позиция', 'позиции', 'позиций')}
      </div>

      <div className="block" style={{ marginTop: 18 }}>
        <div className="block__head"><h3>Состав заказа</h3></div>
        <div className="plist">
          {o.items.map((i, idx) => (
            <div className="citem" key={idx}>
              <span className="thumb">
                <ProductImage src={i.productImageUrl} alt={i.productName} />
              </span>
              <div>
                <div className="citem__top">
                  <div>
                    <div className="citem__name">{i.productName}</div>
                    <div className="pdetail__sku" style={{ marginTop: 4 }}>
                      {i.productSku} · {formatPrice(i.unitPrice)} × {i.quantity}
                    </div>
                  </div>
                  <div className="price">{formatPrice(i.lineTotal)}</div>
                </div>
                {i.productSlug && (
                  <button
                    type="button"
                    className="linkbtn"
                    style={{ color: 'var(--c-accent)' }}
                    onClick={() => navigate(`/p/${i.productSlug}`)}
                  >
                    Открыть товар
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="summary">
        <div className="srow"><span>Получатель</span><span>{o.customerName}</span></div>
        <div className="srow"><span>Телефон</span><span style={{ fontFamily: 'var(--f-mono)' }}>{o.customerPhone}</span></div>
        <div className="srow"><span>Доставка</span><span style={{ textAlign: 'right' }}>{deliveryLine}</span></div>
        {o.comment && <div className="srow"><span>Комментарий</span><span style={{ textAlign: 'right' }}>{o.comment}</span></div>}
        <div className="srow srow--total">
          <span>Итого</span>
          <span className="price">{formatPrice(o.total)}</span>
        </div>
      </div>

      {/* Цены в позициях — снимок на момент покупки, поэтому заказ не меняется задним числом */}
      <p className="field__hint" style={{ padding: '10px var(--gap-page) 0' }}>
        Цены зафиксированы на момент оформления и не меняются при переоценке товара.
      </p>

      <div className="block">
        <div className="block__head"><h3>История</h3></div>
        <ol className="timeline">
          {history.length === 0 ? (
            <li className="timeline__item timeline__item--last">
              <span className="timeline__dot timeline__dot--on" />
              <div>
                <b>{ORDER_STATUS_LABEL[o.status]}</b>
                <small>{formatDateHuman(o.createdAt)}, {timeOf(o.createdAt)}</small>
              </div>
            </li>
          ) : (
            history.map((h, i) => (
              <li
                key={i}
                className={`timeline__item${i === history.length - 1 ? ' timeline__item--last' : ''}`}
              >
                <span className={`timeline__dot${i === history.length - 1 ? ' timeline__dot--on' : ''}`} />
                <div>
                  <b>{ORDER_STATUS_LABEL[h.status]}</b>
                  <small>
                    {formatDateHuman(h.changedAt)}, {timeOf(h.changedAt)}
                    {h.note ? ` · ${h.note}` : ''}
                  </small>
                </div>
              </li>
            ))
          )}
        </ol>
      </div>

      <div className="block" style={{ marginTop: 20, display: 'grid', gap: 10 }}>
        <button type="button" className="secbtn" onClick={() => void repeat()}>
          Повторить заказ
        </button>
        <button
          type="button"
          className="secbtn"
          onClick={() => {
            track('manager_contact', { props: { source: 'order', orderNumber: o.orderNumber } });
            openManagerChat(MANAGER, `Здравствуйте! Вопрос по заказу ${o.orderNumber}`);
          }}
        >
          <IconMessage size={17} />
          Написать менеджеру
        </button>
      </div>

      <div className="pad" />
    </>
  );
}
